"use client"

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"

import { Button } from "@/components/ui/button"
import Link from "next/link"
import React from "react"
import { UserPlus } from "lucide-react"

interface NotRegisteredModalProps {
  open: boolean
  email: string
  onOpenChange: (open: boolean) => void
  onCancel: () => void
  onRegister: () => Promise<void> | void
  isRegistering?: boolean
}

export const NotRegisteredModal: React.FC<NotRegisteredModalProps> = ({
  open,
  email,
  onOpenChange,
  onCancel,
  onRegister,
  isRegistering = false,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-md">
        <DialogHeader className="text-center sm:text-center items-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary mb-2">
            <UserPlus className="h-6 w-6" />
          </div>
          <DialogTitle className="text-xl">Account Not Found</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground pt-1">
            We couldn&apos;t find an account associated with{" "}
            <span className="font-semibold text-foreground break-all">{email}</span>.
            Would you like to register now?
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          <DialogFooter className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-2">
            <Button
              variant="outline"
              type="button"
              onClick={onCancel}
              disabled={isRegistering}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={onRegister}
              disabled={isRegistering}
              className="w-full sm:w-auto"
            >
              {isRegistering ? "Registering..." : "Register"}
            </Button>
          </DialogFooter>

          <div className="text-center text-xs text-muted-foreground pt-1">
            By registering, you agree to our{" "}
            <Link
              href="/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-foreground font-medium"
            >
              Terms of Service
            </Link>{" "}
            and{" "}
            <Link
              href="/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-foreground font-medium"
            >
              Privacy Policy
            </Link>
            .
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default NotRegisteredModal
